/**
 * Boardvorlagen: das Gerüst eines Boards, nicht sein Inhalt.
 *
 * Eine Boardvorlage legt **leere Frames** an — Lage, Größe und Reihenfolge, auf
 * Wunsch auch Name, Gestaltung und Sprechernotiz. Was in den Frames liegt,
 * bleibt draußen. Das ist der ganze Unterschied zur Frame-Vorlage in
 * `template.ts`, und er trägt weit: Ohne Elemente gibt es keine Mediendateien,
 * also nichts zu kopieren, nichts zu belegen und keine Frage, wem eine Datei
 * gehört. Eine Boardvorlage ist ein paar hundert Byte JSON.
 *
 * Wofür sie da ist: Das Wiederkehrende an einer Präsentation ist selten die
 * einzelne Seite, sondern der Ablauf — Titel, Agenda, drei Inhaltsseiten,
 * Fragen. Genau den hält die Boardvorlage fest.
 */
/** Ein Frame in 16:10 — dasselbe Verhältnis wie ein üblicher Beamer. */
export const BOARD_TEMPLATE_FRAME_W = 1920;
export const BOARD_TEMPLATE_FRAME_H = 1200;
/** Abstand zwischen den Frames, damit sie beim Herauszoomen einzeln stehen. */
export const BOARD_TEMPLATE_GAP = 480;
/** Mehr Frames als das ergibt ein Gerüst, durch das niemand mehr präsentiert. */
export const BOARD_TEMPLATE_MAX_FRAMES = 60;
/**
 * Frames der Reihe nach in ein Raster legen.
 *
 * `columns = 0` heißt „alles in eine Reihe" — die übliche Form für einen
 * Vortrag, weil das Board dann von links nach rechts gelesen wird wie der
 * Vortrag selbst.
 */
export function arrangeFrames(names, options = {}) {
    const width = options.width ?? BOARD_TEMPLATE_FRAME_W;
    const height = options.height ?? BOARD_TEMPLATE_FRAME_H;
    const gap = options.gap ?? BOARD_TEMPLATE_GAP;
    const columns = options.columns && options.columns > 0 ? options.columns : names.length || 1;
    return names.map((name, index) => ({
        name,
        x: (index % columns) * (width + gap),
        y: Math.floor(index / columns) * (height + gap),
        w: width,
        h: height,
        style: null,
        notes: '',
    }));
}
/**
 * Schiebt das Gerüst in die linke obere Ecke.
 *
 * Gespeichert wird immer bei (0,0) beginnend: Erst dadurch lässt sich eine
 * Vorlage überhaupt woanders absetzen — beim Anlegen eines Boards im Ursprung,
 * später rechts neben dem, was schon da ist.
 */
export function normalizeBoardTemplate(frames) {
    if (frames.length === 0)
        return [];
    const left = Math.min(...frames.map((frame) => frame.x));
    const top = Math.min(...frames.map((frame) => frame.y));
    return frames.map((frame) => ({ ...frame, x: frame.x - left, y: frame.y - top }));
}
/** Die Fläche, die das Gerüst einnimmt — für Vorschau und Platzierung. */
export function boardTemplateBounds(content) {
    if (content.frames.length === 0)
        return { x: 0, y: 0, w: 0, h: 0 };
    const left = Math.min(...content.frames.map((frame) => frame.x));
    const top = Math.min(...content.frames.map((frame) => frame.y));
    const right = Math.max(...content.frames.map((frame) => frame.x + frame.w));
    const bottom = Math.max(...content.frames.map((frame) => frame.y + frame.h));
    return { x: left, y: top, w: right - left, h: bottom - top };
}
const BUILTIN_SPECS = [
    {
        id: 'builtin:vortrag',
        name: 'bt.talk',
        frames: [
            'bt.talk.title',
            'bt.talk.agenda',
            'bt.talk.topic1',
            'bt.talk.topic2',
            'bt.talk.topic3',
            'bt.talk.conclusion',
            'bt.talk.questions',
        ],
    },
    {
        id: 'builtin:kundentermin',
        name: 'bt.client',
        frames: [
            'bt.client.welcome',
            'bt.client.situation',
            'bt.client.proposal',
            'bt.client.delivery',
            'bt.client.effort',
            'bt.client.next',
        ],
    },
    {
        id: 'builtin:workshop',
        name: 'bt.workshop',
        frames: [
            'bt.workshop.arrive',
            'bt.workshop.goal',
            'bt.workshop.rules',
            'bt.workshop.space1',
            'bt.workshop.space2',
            'bt.workshop.parking',
            'bt.workshop.results',
        ],
    },
    {
        id: 'builtin:lehreinheit',
        name: 'bt.lesson',
        frames: [
            'bt.lesson.start',
            'bt.lesson.goals',
            'bt.lesson.work',
            'bt.lesson.practice',
            'bt.lesson.secure',
            'bt.lesson.outlook',
        ],
    },
    {
        id: 'builtin:moodboard',
        // Nicht zum Vorführen, sondern zum Sammeln: sechs gleich große Flächen im
        // Raster, durch die man auch fahren kann, aber nicht muss.
        name: 'bt.mood',
        frames: [
            'bt.mood.direction',
            'bt.mood.colors',
            'bt.mood.type',
            'bt.mood.imagery',
            'bt.mood.references',
            'bt.mood.boundaries',
        ],
        options: { columns: 3, width: 1400, height: 1400, gap: 320 },
    },
    {
        id: 'builtin:leinwand',
        name: 'bt.canvas',
        frames: ['bt.canvas.frame'],
        options: { width: 5760, height: 3600 },
    },
];
/** Die IDs der mitgelieferten Gerüste — sprachunabhängig. */
export const BUILTIN_BOARD_TEMPLATE_IDS = BUILTIN_SPECS.map((spec) => spec.id);
export function builtinBoardTemplates(t) {
    return BUILTIN_SPECS.map((spec) => ({
        id: spec.id,
        name: t(spec.name),
        content: {
            frames: arrangeFrames(spec.frames.map((key) => t(key)), spec.options ?? {}),
        },
    }));
}
//# sourceMappingURL=boardTemplate.js.map